import { useEffect, useState } from "react";
import { CreditCard, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";

const TIER_LABELS = { signal: "Signal", sentinel: "Sentinel", director: "Director", sovereign: "Sovereign" };
const UPGRADE_PATH = {
  signal: ["sentinel", "director", "sovereign"],
  sentinel: ["director", "sovereign"],
  director: ["sovereign"],
  sovereign: [],
};

export const BillingPanel = () => {
  const [sub, setSub] = useState(null);
  const [busyTier, setBusyTier] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = () => axios.get(`${API}/subscriptions/me`).then((res) => setSub(res.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const subscribeOrChange = async (tier) => {
    setBusyTier(tier);
    try {
      if (sub?.tier === "signal") {
        const { data } = await axios.post(`${API}/subscriptions/checkout`, { tier, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      await axios.post(`${API}/subscriptions/change`, { tier });
      toast.success(`Plan change to ${TIER_LABELS[tier]} is processing.`);
      setTimeout(load, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Could not change plan.");
    } finally {
      setBusyTier(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions/portal`, { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "No billing account yet - subscribe to a paid plan first.");
    } finally {
      setPortalBusy(false);
    }
  };

  const cancel = async () => {
    setCancelBusy(true);
    try {
      await axios.post(`${API}/subscriptions/cancel`);
      toast.success("Your plan will cancel at the end of this billing period.");
      load();
    } catch {
      toast.error("Could not cancel subscription.");
    } finally {
      setCancelBusy(false);
    }
  };

  const resume = async () => {
    setCancelBusy(true);
    try {
      await axios.post(`${API}/subscriptions/resume`);
      toast.success("Subscription resumed.");
      load();
    } catch {
      toast.error("Could not resume subscription.");
    } finally {
      setCancelBusy(false);
    }
  };

  if (!sub) return null;
  const upgrades = sub.is_root_admin ? [] : UPGRADE_PATH[sub.tier] || [];

  return (
    <div className="mt-8 rounded-lg border border-surface-border bg-surface-card p-6" data-testid="billing-panel">
      <div className="mb-1 flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-sovereign" />
        <h2 className="font-heading text-lg font-bold text-white">Sovereign Pulse Subscription</h2>
      </div>
      <div className="mb-4 flex items-center justify-between rounded-md border border-surface-border bg-obsidian p-4">
        <div>
          <p className="text-sm font-semibold text-white" data-testid="billing-current-tier">
            {sub.is_root_admin ? "Root Admin (Unlimited)" : `${TIER_LABELS[sub.tier]} plan`}
          </p>
          <p className="text-xs text-slate-500">
            {sub.scans_limit === null ? "Unlimited scans" : `${sub.scans_used}/${sub.scans_limit} scans used this period`}
            {sub.current_period_end && ` \u00b7 renews ${new Date(sub.current_period_end).toLocaleDateString()}`}
          </p>
        </div>
        {sub.cancel_at_period_end && (
          <span className="flex items-center gap-1 rounded-full bg-warning-amber/15 px-2 py-1 text-xs font-semibold text-warning-amber" data-testid="billing-cancel-pending-badge">
            <AlertTriangle className="h-3 w-3" /> Cancels at period end
          </span>
        )}
      </div>

      {upgrades.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {upgrades.map((tier) => (
            <Button
              key={tier}
              size="sm"
              disabled={busyTier === tier}
              onClick={() => subscribeOrChange(tier)}
              className="gap-2 bg-sovereign text-white hover:bg-sovereign/90"
              data-testid={`billing-upgrade-${tier}-btn`}
            >
              {busyTier === tier ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Upgrade to {TIER_LABELS[tier]}
            </Button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={portalBusy}
          onClick={openPortal}
          className="gap-2 border-surface-border text-slate-200 hover:text-white"
          data-testid="billing-manage-portal-btn"
        >
          {portalBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />} Manage Billing &amp; Invoices
        </Button>
        {sub.tier !== "signal" && !sub.cancel_at_period_end && (
          <Button
            size="sm"
            variant="outline"
            disabled={cancelBusy}
            onClick={cancel}
            className="gap-2 border-sovereign/40 text-sovereign hover:bg-sovereign/10"
            data-testid="billing-cancel-btn"
          >
            Cancel Subscription
          </Button>
        )}
        {sub.cancel_at_period_end && (
          <Button
            size="sm"
            disabled={cancelBusy}
            onClick={resume}
            className="gap-2 bg-truth-green text-obsidian hover:bg-truth-green/90"
            data-testid="billing-resume-btn"
          >
            Resume Subscription
          </Button>
        )}
      </div>

      {sub.invoices?.length > 0 && (
        <div className="mt-4" data-testid="billing-invoices-list">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Recent Invoices</p>
          <ul className="space-y-1">
            {sub.invoices.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between text-xs text-slate-400" data-testid={`billing-invoice-${inv.id}`}>
                <span>
                  {new Date(inv.created).toLocaleDateString()} &mdash; ${inv.amount_paid.toFixed(2)} ({inv.status})
                </span>
                {inv.hosted_invoice_url && (
                  <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-openai-cyan hover:underline">
                    View
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
